import { ErrorContent } from "./ErrorContent";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { track } from "@/utils/events/tracker";
import { TriangleAlertIcon, XIcon } from "lucide-react";
import { useRef } from "react";

export enum StoryboardErrorType {
  RUNTIME_ERROR = "RUNTIME_ERROR",
  NON_RESPONSIVE = "NON_RESPONSIVE",
  FAILED_TO_LOAD = "FAILED_TO_LOAD",
}

interface ErrorOverlayProps {
  message?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  stack?: string;
  storyboardId?: string;
  errorType?: StoryboardErrorType;
  onClose?: () => void;

  // Of the container
  containerWidth?: number;
  containerHeight?: number;
}

export function ErrorOverlay({
  message = "An error occurred",
  filename = "unknown",
  lineno = 1,
  colno = 1,
  stack,
  storyboardId,
  errorType = StoryboardErrorType.RUNTIME_ERROR,
  onClose,
  containerWidth,
  containerHeight,
}: ErrorOverlayProps) {
  const setSendChatMessage = useAiContextStore(
    (state) => state.setSendChatMessage,
  );
  const setNewLayout = useLayoutStore((state) => state.setNewLayout);
  const isCompactMode =
    containerWidth &&
    containerHeight &&
    (containerWidth < 100 || containerHeight < 100);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleFixWithAI = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const prompt = `Please make a code change to fix this error in my code in storyboard with id ${storyboardId}:\n\n${message} at ${filename}:${lineno}:${colno} \n\n${stack}`;

    const sent = setSendChatMessage({
      sendChatMessage: prompt,
      sendChatMessageFiles: [],
      isFixWithAi: true,
      includeErrorsIfAny: true,
      isFreePrompt: true,
      refreshAfterResponse: true,
    });
    if (sent) {
      setNewLayout(LeftTab.AIChat);
      track("AI_PROMPT_REQUESTED", { type: "AI_CHAT_FIX_ERROR", prompt });
    }
  };

  // Calculate zoom scale with larger range and moderate effect
  // Scales from 0.5x (when zoomed in) to 2x (when zoomed out)
  const zoomScale = `clamp(0.7, calc(0.7 + (1 - var(--canvas-zoom, 1)) * 1), 2)`;

  return (
    <div
      ref={containerRef}
      className="w-full h-full bg-black/60 backdrop-blur-xs flex items-center justify-center overflow-hidden p-2"
    >
      {isCompactMode ? (
        <div className="flex flex-row gap-2">
          <div
            data-testid="fix-with-ai-button"
            className="bg-red-500 rounded-md p-1.5 cursor-pointer hover:bg-red-600 transition-colors pointer-events-auto shadow-lg"
            onClick={handleFixWithAI}
            data-tooltip-id="tooltip"
            data-tooltip-html={`
            <div class="max-w-xs">
              <div class="font-semibold mb-1 text-white">Error: ${message
                .replace(/'/g, "&#39;")
                .replace(/"/g, "&quot;")}</div>
              <div class="text-xs opacity-80">at ${filename}:${lineno}:${colno}</div>
              ${
                stack
                  ? `<div class="text-xs mt-2 font-mono opacity-70 max-h-32 overflow-hidden">${stack
                      .substring(0, 200)
                      .replace(/'/g, "&#39;")
                      .replace(/"/g, "&quot;")}${
                      stack.length > 200 ? "..." : ""
                    }</div>`
                  : ""
              }
              <div class="mt-2 text-xs font-medium text-blue-300">Click to fix with AI</div>
            </div>
          `}
            data-tooltip-place="top"
          >
            <TriangleAlertIcon className="w-4 h-4 stroke-white-500" />
          </div>
          {onClose && (
            <div
              className="bg-secondary-0 rounded-md p-1.5 cursor-pointer hover:bg-secondary-400 transition-colors pointer-events-auto shadow-lg"
              data-tooltip-id="tooltip"
              data-tooltip-html={`Close error overlay`}
              data-tooltip-place="top"
              onClick={onClose}
            >
              <XIcon className="w-4 h-4 stroke-white" />
            </div>
          )}
        </div>
      ) : (
        <div
          className="w-full flex flex-col"
          style={{
            maxWidth: `calc(500px * ${zoomScale})`,
            maxHeight: "90%",
            gap: `calc(8px * ${zoomScale})`,
          }}
        >
          <ErrorContent
            message={message}
            filename={filename}
            lineno={lineno}
            colno={colno}
            stack={stack}
            onClose={onClose}
            onFixWithAI={handleFixWithAI}
            zoomScale={zoomScale}
          />
        </div>
      )}
    </div>
  );
}

export default ErrorOverlay;
